import { useMemo, useState } from "react";

const formatDateTime = (value) => {
  if (!value) return "-";
  return String(value).replace("T", " ").slice(0, 16);
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

function UserManagementTable({
  loading,
  users,
  currentUser,
  isSuperAdmin,
  canCreateUsers,
  visibleRoles,
  onCreateUser,
  onApprove,
  onRoleChange,
  onCompanyChange,
  onContactChange,
  onVerifyContact,
  onPasswordReset,
  onDeleteUser,
  onHold,
  onNotice,
}) {
  // ★확인창이 열려 있는 동안 고른 값을 유지한다 (2026-08-27).
  //   select 가 user.role 에 묶여 있어서, 고르는 즉시 옛 값으로 되돌아갔다.
  //   확인창은 화면 밖에 있었으므로 쓰는 사람에게는 "변경해도 원상복구된다"로만 보였다.
  const [pendingRoles, setPendingRoles] = useState({});
  const [query, setQuery] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortKey, setSortKey] = useState("created_desc");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState("");
  const [createForm, setCreateForm] = useState({
    userid: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "member",
    company: currentUser?.company || "",
    is_approved: true,
  });

  const roleLabels = useMemo(
    () => Object.fromEntries(visibleRoles.map((role) => [role.id, role.label])),
    [visibleRoles]
  );

  const companies = useMemo(() => {
    const names = users.map((user) => user.company || "미지정");
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, "ko-KR"));
  }, [users]);

  const summary = useMemo(() => {
    const total = users.length;
    const pending = users.filter((user) => !user.is_approved).length;
    const approved = total - pending;
    const admins = users.filter((user) => ["admin", "company_owner", "company_admin"].includes(user.role)).length;
    const missingContact = users.filter((user) => !user.email || !user.phone).length;
    const verified = users.filter((user) => user.email_verified && user.phone_verified).length;
    return { total, pending, approved, admins, missingContact, verified };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const keyword = normalizeText(query);
    const result = users.filter((user) => {
      const company = user.company || "미지정";
      const verified = user.email_verified && user.phone_verified;
      const haystack = [user.userid, user.name, user.email, user.phone, company, roleLabels[user.role] || user.role]
        .map(normalizeText)
        .join(" ");
      if (keyword && !haystack.includes(keyword)) return false;
      if (companyFilter !== "all" && company !== companyFilter) return false;
      if (roleFilter !== "all" && (user.role || "member") !== roleFilter) return false;
      if (statusFilter === "pending" && user.is_approved) return false;
      if (statusFilter === "approved" && !user.is_approved) return false;
      if (statusFilter === "unverified" && verified) return false;
      if (statusFilter === "verified" && !verified) return false;
      return true;
    });

    return [...result].sort((a, b) => {
      if (sortKey === "created_asc") return String(a.created_at || "").localeCompare(String(b.created_at || ""));
      if (sortKey === "userid") return String(a.userid || "").localeCompare(String(b.userid || ""), "ko-KR");
      if (sortKey === "company") return String(a.company || "").localeCompare(String(b.company || ""), "ko-KR");
      if (sortKey === "status") return Number(a.is_approved) - Number(b.is_approved);
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
  }, [users, query, companyFilter, roleFilter, statusFilter, sortKey, roleLabels]);

  const selectedUsers = filteredUsers.filter((user) => selectedIds.includes(user.userid));
  const allVisibleSelected = filteredUsers.length > 0 && filteredUsers.every((user) => selectedIds.includes(user.userid));

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredUsers.some((user) => user.userid === id)));
      return;
    }
    setSelectedIds((prev) => [...new Set([...prev, ...filteredUsers.map((user) => user.userid)])]);
  };

  const toggleUser = (userid) => {
    setSelectedIds((prev) => prev.includes(userid) ? prev.filter((id) => id !== userid) : [...prev, userid]);
  };

  const updateCreateForm = (key, value) => {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
  };

  const submitCreateUser = async (event) => {
    event.preventDefault();
    if (!createForm.userid.trim() || !createForm.name.trim() || !createForm.email.trim() || !createForm.phone.trim() || !createForm.password) {
      onNotice?.("아이디, 이름, 이메일, 전화번호, 임시 비밀번호를 모두 입력해주세요.");
      return;
    }
    if (createForm.password.length < 8) {
      onNotice?.("임시 비밀번호는 8자 이상이어야 합니다.");
      return;
    }
    const ok = await onCreateUser?.({
      ...createForm,
      company: isSuperAdmin ? createForm.company : currentUser?.company,
    });
    if (ok) {
      setCreateForm({
        userid: "",
        name: "",
        email: "",
        phone: "",
        password: "",
        role: "member",
        company: currentUser?.company || "",
        is_approved: true,
      });
    }
  };

  const runBulkAction = async (action) => {
    const targets = selectedUsers.filter((user) => user.userid !== currentUser?.userid && user.userid !== "admin");
    if (targets.length === 0) {
      onNotice?.("처리 가능한 선택 회원이 없습니다.");
      return;
    }
    setBulkAction(action);
  };

  const executeBulkAction = async () => {
    const targets = selectedUsers.filter((user) => user.userid !== currentUser?.userid && user.userid !== "admin");
    for (const user of targets) {
      if (bulkAction === "approve" && !user.is_approved) await onApprove(user.userid, { silent: true });
      if (bulkAction === "hold" && user.is_approved) await onHold(user.userid, { silent: true });
    }
    setSelectedIds([]);
    setBulkAction("");
    onNotice?.(bulkAction === "approve" ? "선택 회원을 승인했습니다." : "선택 회원의 승인을 해제했습니다.");
  };

  return (
    <>
      <div className="admin-section-title">
        <div>
          <h3>회원 관리</h3>
          <p>{isSuperAdmin ? "전체 회원의 가입 승인, 권한, 회사 소속과 계정 처리를 관리합니다." : "협력사 대표/관리자는 자기 회사 직원의 승인, 권한, 연락처와 계정을 관리합니다."}</p>
        </div>
        <div className="member-action-row">
          <button className="admin-approve-button" disabled={selectedUsers.length === 0} onClick={() => runBulkAction("approve")}>
            선택 승인
          </button>
          <button className="admin-hold-button" disabled={selectedUsers.length === 0} onClick={() => runBulkAction("hold")}>
            선택 승인 해제
          </button>
        </div>
      </div>

      {canCreateUsers && (
        <form className="member-create-panel" onSubmit={submitCreateUser}>
          <div className="member-create-title">
            <strong>직원 직접 등록</strong>
            <span>{isSuperAdmin ? "회사와 권한을 지정해 계정을 생성합니다." : `${currentUser?.company || "내 회사"} 직원 계정을 생성하고 승인 처리합니다.`}</span>
          </div>
          <div className="member-create-grid">
            <input className="member-search-input" value={createForm.userid} onChange={(event) => updateCreateForm("userid", event.target.value)} placeholder="아이디" />
            <input className="member-search-input" value={createForm.name} onChange={(event) => updateCreateForm("name", event.target.value)} placeholder="이름" />
            <input className="member-search-input" value={createForm.email} onChange={(event) => updateCreateForm("email", event.target.value)} placeholder="이메일" />
            <input className="member-search-input" value={createForm.phone} onChange={(event) => updateCreateForm("phone", event.target.value)} placeholder="전화번호" />
            <input className="member-search-input" type="password" value={createForm.password} onChange={(event) => updateCreateForm("password", event.target.value)} placeholder="임시 비밀번호 8자 이상" />
            {isSuperAdmin && (
              <input className="member-search-input" value={createForm.company} onChange={(event) => updateCreateForm("company", event.target.value)} placeholder="회사명" />
            )}
            <select className="member-filter-select" value={createForm.role} onChange={(event) => updateCreateForm("role", event.target.value)}>
              {visibleRoles.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
            </select>
            <label className="member-create-check">
              <input type="checkbox" checked={createForm.is_approved} onChange={(event) => updateCreateForm("is_approved", event.target.checked)} />
              <span>생성 즉시 승인</span>
            </label>
            <button className="admin-approve-button" type="submit">직원 등록</button>
          </div>
        </form>
      )}

      {bulkAction && (
        <div className="member-bulk-confirm">
          <div>
            <strong>{bulkAction === "approve" ? "선택 회원 승인" : "선택 회원 승인 해제"}</strong>
            <span>{selectedUsers.length}명을 처리합니다. 본인 계정과 기본 admin 계정은 자동 제외됩니다.</span>
          </div>
          <button type="button" className="admin-approve-button" onClick={executeBulkAction}>적용</button>
          <button type="button" className="admin-hold-button" onClick={() => setBulkAction("")}>취소</button>
        </div>
      )}

      <div className="member-summary-grid">
        <div className="member-summary-card"><span>전체 회원</span><strong>{summary.total}</strong></div>
        <div className="member-summary-card warn"><span>승인 대기</span><strong>{summary.pending}</strong></div>
        <div className="member-summary-card ok"><span>승인 완료</span><strong>{summary.approved}</strong></div>
        <div className="member-summary-card ok"><span>연락처 인증</span><strong>{summary.verified}</strong></div>
        <div className="member-summary-card"><span>관리 권한</span><strong>{summary.admins}</strong></div>
        <div className="member-summary-card danger"><span>연락처 미완성</span><strong>{summary.missingContact}</strong></div>
      </div>

      <div className="member-toolbar">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="아이디, 이름, 이메일, 전화번호, 회사 검색"
          className="member-search-input"
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="member-filter-select">
          <option value="all">전체 상태</option>
          <option value="pending">승인 대기</option>
          <option value="approved">승인 완료</option>
          <option value="unverified">인증 미완료</option>
          <option value="verified">인증 완료</option>
        </select>
        <select value={companyFilter} onChange={(event) => setCompanyFilter(event.target.value)} className="member-filter-select">
          <option value="all">전체 회사</option>
          {companies.map((company) => <option key={company} value={company}>{company}</option>)}
        </select>
        <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} className="member-filter-select">
          <option value="all">전체 권한</option>
          {visibleRoles.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
        </select>
        <select value={sortKey} onChange={(event) => setSortKey(event.target.value)} className="member-filter-select">
          <option value="created_desc">최근 가입순</option>
          <option value="created_asc">오래된 가입순</option>
          <option value="userid">아이디순</option>
          <option value="company">회사순</option>
          <option value="status">승인 대기 우선</option>
        </select>
      </div>

      <div className="member-result-meta">
        <span>표시 {filteredUsers.length}명 / 전체 {users.length}명</span>
        {selectedUsers.length > 0 && <strong>{selectedUsers.length}명 선택됨</strong>}
      </div>

      {!loading && filteredUsers.length === 0 ? (
        <div className="admin-empty">표시할 회원이 없습니다.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="member-check-cell">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} aria-label="표시 회원 전체 선택" />
                </th>
                <th>아이디</th>
                <th>이름</th>
                <th>연락처</th>
                <th>인증</th>
                <th>회사</th>
                <th>권한</th>
                <th>상태</th>
                <th>가입일</th>
                <th>처리</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const verified = user.email_verified && user.phone_verified;
                return (
                  <tr key={user.id}>
                    <td className="member-check-cell">
                      <input type="checkbox" checked={selectedIds.includes(user.userid)} onChange={() => toggleUser(user.userid)} aria-label={`${user.userid} 선택`} />
                    </td>
                    <td>
                      <strong className="member-userid">{user.userid}</strong>
                      {user.userid === currentUser?.userid && <span className="member-current-badge">나</span>}
                    </td>
                    <td>{user.name || "-"}</td>
                    <td>
                      <div className="member-contact-cell">
                        <span>{user.email || "이메일 없음"}</span>
                        <small>{user.phone || "전화번호 없음"}</small>
                      </div>
                    </td>
                    <td>
                      <div className="member-contact-cell">
                        <span className={`member-verify-badge ${user.email_verified ? "ok" : "pending"}`}>이메일 {user.email_verified ? "확인" : "미확인"}</span>
                        <span className={`member-verify-badge ${user.phone_verified ? "ok" : "pending"}`}>전화 {user.phone_verified ? "확인" : "미확인"}</span>
                      </div>
                    </td>
                    <td>
                      <span>{user.company || "-"}</span>
                      {isSuperAdmin && (
                        <button className="admin-inline-button" onClick={() => onCompanyChange(user.userid, user.company)}>
                          변경
                        </button>
                      )}
                    </td>
                    <td>
                      <select
                        className="admin-role-select"
                        value={pendingRoles[user.userid] ?? (user.role || "member")}
                        onChange={async (event) => {
                          const next = event.target.value;
                          setPendingRoles((prev) => ({ ...prev, [user.userid]: next }));
                          try {
                            await onRoleChange(user.userid, next);
                          } finally {
                            // 목록을 다시 읽어 온 뒤에 풀어야 깜빡임이 없다
                            setPendingRoles((prev) => {
                              const rest = { ...prev };
                              delete rest[user.userid];
                              return rest;
                            });
                          }
                        }}
                        disabled={user.userid === currentUser?.userid || user.userid === "admin"}
                      >
                        {visibleRoles.map((role) => (
                          <option key={role.id} value={role.id}>{role.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={`member-status-badge ${user.is_approved ? "approved" : "pending"}`}>
                        {user.is_approved ? "승인 완료" : "승인 대기"}
                      </span>
                    </td>
                    <td>{formatDateTime(user.created_at)}</td>
                    <td>
                      <div className="member-row-actions">
                        <button onClick={() => onContactChange(user.userid, user)} className="admin-inline-button">
                          수정
                        </button>
                        <button onClick={() => onVerifyContact(user.userid, user)} className="admin-inline-button" disabled={verified}>
                          인증 확인
                        </button>
                        <button onClick={() => onPasswordReset(user.userid)} className="admin-inline-button" disabled={user.userid === currentUser?.userid}>
                          비번 초기화
                        </button>
                      </div>
                      {!user.is_approved ? (
                        <button onClick={() => onApprove(user.userid)} className="admin-approve-button">
                          승인
                        </button>
                      ) : (
                        <button onClick={() => onHold(user.userid)} className="admin-hold-button" disabled={user.userid === currentUser?.userid || user.userid === "admin"}>
                          승인 해제
                        </button>
                      )}
                      <button onClick={() => onDeleteUser(user.userid)} className="admin-danger-button member-delete-button" disabled={user.userid === currentUser?.userid || user.userid === "admin"}>
                        탈퇴
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export default UserManagementTable;
